import { zodResolver } from '@hookform/resolvers/zod'
import { CheckIcon, GearSixIcon } from '@phosphor-icons/react'
import { FormProvider, type Resolver, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useGetMaintenanceSettings } from '@/api/maintenance/get-maintenance-settings'
import { useUpdateMaintenanceSettings } from '@/api/maintenance/update-maintenance-settings'
import { WeekSchedule } from '@/components/business/week-schedule'
import { SelectFormField } from '@/components/form/select-form-field'
import { SwitchFormField } from '@/components/form/switch-form-field'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { Modal } from '@/components/ui/modal'
import { toastFactory } from '@/factories'
import type { MaintenanceSettings as MaintenanceSettingsModel } from '@/models'
import { getApiErrorMessage } from '@/utils'

import * as S from './maintenance-settings.styled'
import {
  type MaintenanceSettingsForm,
  type MaintenanceSettingsProps,
  makeMaintenanceSettingsFormSchema,
} from './maintenance-settings.types'
import {
  getSlotLengthOptions,
  getWeekScheduleLabels,
  mapFormToSettings,
  mapSettingsToForm,
} from './maintenance-settings.utils'

type MaintenanceSettingsEditorProps = MaintenanceSettingsProps & {
  settings: MaintenanceSettingsModel
}

const MaintenanceSettingsEditor = ({
  onClose,
  buildingId,
  settings,
}: MaintenanceSettingsEditorProps) => {
  const { t } = useTranslation(['maintenance'])
  const { mutateAsync: updateSettings, isPending: isSaving } =
    useUpdateMaintenanceSettings(buildingId)

  const form = useForm<MaintenanceSettingsForm>({
    defaultValues: mapSettingsToForm(settings),
    mode: 'onTouched',
    resolver: zodResolver(
      makeMaintenanceSettingsFormSchema(t),
    ) as Resolver<MaintenanceSettingsForm>,
  })
  const {
    formState: { errors, isSubmitted },
  } = form

  const suggestedTimeframeEnabled = useWatch({
    control: form.control,
    name: 'suggestedTimeframeEnabled',
  })
  const slotLength = useWatch({ control: form.control, name: 'slotLength' })
  const requiredScheduledDate = useWatch({ control: form.control, name: 'requiredScheduledDate' })
  const requiredAssignee = useWatch({ control: form.control, name: 'requiredAssignee' })

  // Note: "at least one day" is an array-level issue - RHF files it under `.root` once the
  // field array is registered, or on the array itself otherwise.
  const daysError = isSubmitted
    ? ((errors.businessHours as { message?: string } | undefined)?.message ??
      errors.businessHours?.root?.message)
    : undefined

  // Note: nothing is sent to BE until Save - the form only holds local edits before that.
  const handleSave = async (formData: MaintenanceSettingsForm) => {
    try {
      await updateSettings(mapFormToSettings(formData, buildingId))
      toastFactory.success(t('maintenance:settings.toastSaveSuccess'))
      onClose()
    } catch {
      form.resetField('requiredScheduledDate')
      form.resetField('requiredAssignee')
      toastFactory.error(t('maintenance:settings.toastSaveError'))
    }
  }

  return (
    <FormProvider {...form}>
      <Modal
        autoWidth
        title={t('maintenance:settings.title')}
        headerIcon={GearSixIcon}
        footer={
          <>
            <Button use="secondary" onClick={onClose}>
              {t('maintenance:settings.cancel')}
            </Button>
            <Button use="primary" loading={isSaving} onClick={form.handleSubmit(handleSave)}>
              {t('maintenance:settings.save')}
              <CheckIcon />
            </Button>
          </>
        }
        onClose={onClose}
      >
        <S.Body>
          <S.Toggles>
            <SwitchFormField
              name="requiredScheduledDate"
              label={t('maintenance:settings.requiredScheduledDate.label')}
              tooltip={t('maintenance:settings.requiredScheduledDate.helperText')}
              inlineLabel={
                requiredScheduledDate
                  ? t('maintenance:settings.common.yes')
                  : t('maintenance:settings.common.no')
              }
            />

            <Divider light />
            <SwitchFormField
              name="requiredAssignee"
              label={t('maintenance:settings.requiredAssignee.label')}
              tooltip={t('maintenance:settings.requiredAssignee.helperText')}
              inlineLabel={
                requiredAssignee
                  ? t('maintenance:settings.common.yes')
                  : t('maintenance:settings.common.no')
              }
            />

            <Divider light />
            <SwitchFormField
              name="suggestedTimeframeEnabled"
              label={t('maintenance:settings.suggestedTimeframe.label')}
              inlineLabel={
                suggestedTimeframeEnabled
                  ? t('maintenance:settings.common.yes')
                  : t('maintenance:settings.common.no')
              }
            />
          </S.Toggles>

          {suggestedTimeframeEnabled && (
            <>
              <SelectFormField
                name="slotLength"
                label={t('maintenance:settings.slotLength.label')}
                options={getSlotLengthOptions(t)}
              />
              <WeekSchedule
                name="businessHours"
                labels={getWeekScheduleLabels(t)}
                daysError={daysError}
                variant="maintenance"
                minDurationMinutes={slotLength ? slotLength * 60 : undefined}
              />
            </>
          )}
        </S.Body>
      </Modal>
    </FormProvider>
  )
}

export const MaintenanceSettings = ({ buildingId, ...props }: MaintenanceSettingsProps) => {
  const { t } = useTranslation(['maintenance'])
  const { data: settings, isPending, isError, error } = useGetMaintenanceSettings({ buildingId })

  if (isPending) return null

  // Note: no fallback to empty settings on a failed fetch - saving those would wipe the
  // property's real schedule.
  if (isError) {
    return (
      <Modal
        title={t('maintenance:settings.title')}
        headerIcon={GearSixIcon}
        footer={
          <Button use="secondary" onClick={props.onClose}>
            {t('maintenance:settings.cancel')}
          </Button>
        }
        onClose={props.onClose}
      >
        <Alert type="error" title={getApiErrorMessage(error)} />
      </Modal>
    )
  }

  return (
    <MaintenanceSettingsEditor
      key={buildingId}
      settings={settings}
      buildingId={buildingId}
      {...props}
    />
  )
}
